import React, { useEffect, useState, createContext, useContext } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import BrandLogo from "./brand/Logo";
import { auth } from "../lib/api";
import { portalApi } from "../lib/portalApi";
import { Toaster } from "./ui/sonner";
import {
  LayoutDashboard, FileBarChart, Upload, Users, GraduationCap,
  LogOut, ShieldCheck, Briefcase, Settings, BookOpen, ScrollText, Inbox,
  Menu, X, Globe, TrafficCone,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const PortalCtx = createContext(null);
export const usePortal = () => useContext(PortalCtx);

const ROLE_BADGE = {
  super_admin: { label: "Super Admin", cls: "text-[color:var(--auditiq-critical)] border-[color:var(--auditiq-critical)]" },
  sales_demo:  { label: "Sales Demo",  cls: "text-electric border-electric" },
  municipality_user: { label: "Municipality",  cls: "text-gold border-gold" },
  department_user:   { label: "Department",    cls: "text-gold border-gold" },
  auditor:     { label: "Auditor",     cls: "text-cream border-cream-dim" },
  viewer:      { label: "Viewer",      cls: "text-cream-dim border-steel" },
  certification_student: { label: "Student", cls: "text-cream-dim border-steel" },
  student:      { label: "Academy Student", cls: "text-gold border-gold" },
  teacher_aide: { label: "Teacher's Aide",  cls: "text-electric border-electric" },
  parent:       { label: "Parent",          cls: "text-gold border-gold" },
};

export const PortalLayout = ({ children }) => {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [activeOrg, setActiveOrg] = useState(() => localStorage.getItem("auditiq_active_org") || "");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [platformPerms, setPlatformPerms] = useState(null);

  useEffect(() => {
    if (!auth.token()) { nav("/portal/login"); return; }
    portalApi.me().then(m => {
      setMe(m);
      portalApi.orgs().then(r => {
        const list = r.organizations || [];
        setOrgs(list);
        if (!activeOrg && list.length) {
          const def = m.organization_id && list.find(o => o.id === m.organization_id) ? m.organization_id : list[0].id;
          setActiveOrg(def);
          localStorage.setItem("auditiq_active_org", def);
        }
      });
    }).catch(() => { auth.clear(); nav("/portal/login"); });
    // Fetch platform permissions + demo_mode flag for the global banner
    import("../lib/api").then(({ api }) => {
      api.get("/auditiq/my-permissions").then(r => setPlatformPerms(r.data)).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeOrg = (id) => { setActiveOrg(id); localStorage.setItem("auditiq_active_org", id); };

  const resetDemo = async () => {
    if (!activeOrg) return toast.error("Select an organization first");
    try {
      const r = await portalApi.resetDemo(activeOrg);
      toast.success(`Demo reset: ${r.cleared.uploads + r.cleared.reports + r.cleared.analyses} items cleared`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };

  const signOut = () => { auth.clear(); localStorage.removeItem("auditiq_active_org"); nav("/portal/login"); };

  if (!me) return (
    <div className="min-h-screen flex items-center justify-center bg-navy">
      <div className="text-cream-dim text-sm">Loading portal…</div>
    </div>
  );

  const badge = ROLE_BADGE[me.role] || ROLE_BADGE.viewer;
  const canResetDemo = me.permissions?.reset_demo;
  const canManageUsers = me.permissions?.manage_users;
  const isSuperAdmin = me.role === "super_admin";
  const canViewAuditLogs = me.permissions?.view_audit_logs;
  const isAcademyRole = ["student", "teacher_aide", "parent"].includes(me.role);

  // Build role-appropriate nav
  const navItems = isAcademyRole
    ? [
        { to: me.role === "student" ? "/portal/student"
            : me.role === "teacher_aide" ? "/portal/teacher-aide"
            : "/portal/parent",
          label: "Dashboard", Icon: LayoutDashboard, end: true },
        ...(me.role === "student" || me.role === "teacher_aide"
          ? [{ to: "/portal/curriculum", label: "Curriculum", Icon: BookOpen }]
          : []),
        ...(me.role === "student"
          ? [{ to: "/portal/certification", label: "Certification", Icon: GraduationCap }]
          : []),
        { to: "/portal/tickets", label: "Tickets", Icon: Inbox },
      ]
    : [
        { to: "/portal", label: "Dashboard",      Icon: LayoutDashboard, end: true },
        { to: "/auditiq/crawler",   label: "AI Web Crawler", Icon: Globe },
        { to: "/auditiq/upload",    label: "Upload Audit",  Icon: Upload },
        { to: "/auditiq/history",   label: "Audit History", Icon: FileBarChart },
        ...(["super_admin", "sales_demo", "municipality_user", "auditor", "department_user"].includes(me.role)
          ? [{ to: "/portal/traffic/recommendations", label: "Traffic Approvals", Icon: TrafficCone }]
          : []),
        ...(me.role !== "sales_demo"
          ? [
              { to: "/portal/auditiq/reports", label: "AuditIQ Reports", Icon: FileBarChart },
              { to: "/portal/reports", label: "Reports", Icon: FileBarChart },
            ]
          : []),
        { to: "/portal/curriculum", label: "Curriculum", Icon: BookOpen },
        { to: "/portal/certification", label: "Certification", Icon: GraduationCap },
        { to: "/portal/tickets", label: "Tickets", Icon: Inbox },
        ...(canManageUsers ? [{ to: "/portal/users", label: "User Management", Icon: Users }] : []),
        ...(["super_admin", "master_admin"].includes(me.role)
          ? [{ to: "/portal/admin/reports", label: "Admin Reports", Icon: FileBarChart }]
          : []),
        ...(canViewAuditLogs ? [{ to: "/portal/audit-logs", label: "Audit Logs", Icon: ScrollText }] : []),
        ...(isSuperAdmin ? [{ to: "/portal/admin", label: "Admin Hub", Icon: Settings }] : []),
      ];

  const sidebarContent = (
    <>
      <div className="p-5 border-b border-gold-dim/20 flex items-center justify-between">
        <Link to="/" onClick={() => setMobileNavOpen(false)}><BrandLogo /></Link>
        <button onClick={() => setMobileNavOpen(false)}
          className="lg:hidden text-cream-dim hover:text-gold p-1"
          aria-label="Close menu" data-testid="portal-mobile-close">
          <X size={20} />
        </button>
      </div>
      <div className="px-5 py-3 border-b border-gold-dim/20 text-[10px] tracking-[0.28em] uppercase text-electric flex items-center gap-2">
        <ShieldCheck size={12} /> Secure Portal
      </div>
      <nav className="p-3 flex-1 overflow-y-auto">
        {navItems.map(({ to, label, Icon, end }) => (
          <NavLink key={to} to={to} end={end}
            onClick={() => setMobileNavOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded text-sm transition-colors ${
                isActive ? "bg-navy-soft text-gold border-l-2 border-gold" : "text-cream-dim hover:text-gold"
              }`}
            data-testid={`portal-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <Icon size={16} />{label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-gold-dim/20">
        <div className="text-xs text-cream truncate">{me.name}</div>
        <div className="text-[10px] text-steel mt-1 truncate">{me.email}</div>
        <span className={`inline-block mt-2 text-[10px] px-2 py-0.5 rounded border uppercase tracking-wider ${badge.cls}`}>
          {badge.label}
        </span>
        <Button onClick={signOut} className="btn-ghost-gold h-9 w-full mt-3 text-xs" data-testid="portal-signout">
          <LogOut size={12} className="mr-1.5" /> Sign out
        </Button>
      </div>
    </>
  );

  return (
    <PortalCtx.Provider value={{ me, orgs, activeOrg, setActiveOrg: onChangeOrg }}>
      <div className="min-h-screen flex bg-navy">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex w-72 flex-col border-r border-gold-dim/25 bg-navy-deep/80">
          {sidebarContent}
        </aside>

        {/* Mobile drawer */}
        {mobileNavOpen && (
          <>
            <div onClick={() => setMobileNavOpen(false)}
              className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" />
            <aside className="lg:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] flex flex-col border-r border-gold-dim/25 bg-navy-deep" data-testid="portal-mobile-drawer">
              {sidebarContent}
            </aside>
          </>
        )}

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Persistent demo-mode banner — shows when demo_mode ON or role is sales_demo */}
          {(platformPerms?.demo_mode || me?.role === "sales_demo") && (
            <div
              className="bg-gold/15 border-b border-gold text-gold px-4 py-2 text-sm font-medium text-center"
              data-testid="portal-demo-mode-banner"
            >
              Demo Mode — You are viewing simulated data.
            </div>
          )}
          <header className="sticky top-0 z-30 bg-navy-deep/80 backdrop-blur border-b border-gold-dim/25">
            <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <button onClick={() => setMobileNavOpen(true)}
                  className="lg:hidden text-gold p-1" aria-label="Open menu"
                  data-testid="portal-mobile-menu-btn">
                  <Menu size={22} />
                </button>
                <BrandLogo />
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                {canResetDemo && (
                  <Button onClick={resetDemo} className="btn-ghost-gold h-9 px-3 sm:px-4 text-xs" data-testid="portal-reset-demo">
                    <span className="hidden sm:inline">Reset Demo</span>
                    <span className="sm:hidden">Reset</span>
                  </Button>
                )}
                <Link to="/"><Button className="btn-ghost-gold h-9 px-3 sm:px-4 text-xs" data-testid="portal-to-public">
                  <span className="hidden sm:inline">Public Site</span>
                  <span className="sm:hidden">Public</span>
                </Button></Link>
              </div>
            </div>
          </header>
          <main className="flex-1 p-4 sm:p-6 max-w-[1400px] w-full mx-auto">{children}</main>
          <Toaster position="top-right" richColors theme="dark" />
        </div>
      </div>
    </PortalCtx.Provider>
  );
};

export default PortalLayout;
